import { useCallback } from "react";

import { Box } from "@mui/system";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { FormProvider, useForm } from "react-hook-form";
import { Button, CircularProgress, Tooltip, Typography } from "@mui/material";

import { useCreateTempWorker } from "core/temp_workers/hooks";
import { CreateForm } from "./form";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  onCreate?: () => void;
}

export function CandidateCreateForm({
  onCreate,
}: Props) {
  const queryClient = useQueryClient();
  // Form Mutation
  const mutation = useCreateTempWorker({
    onSuccess: (data: any) => {
      toast.success("Le candidat a été créé");
      queryClient.invalidateQueries(['candidates']);
      onCreate?.();
    },
    onError: (error: any) => {
      var errorMessage = "Une Erreur est survenue";

      if (error instanceof AxiosError) {
        // Validation errors (422) carry `errors`; a 500 or a network failure does not.
        const { full_messages, ...errors } = error.response?.data?.errors ?? {};
        errorMessage = full_messages?.join(", ") || (error.response?.status === 500 ? "Erreur serveur : le candidat n'a pas pu être créé." : "Une Erreur est survenue");
        Object.entries<Array<string>>(errors).forEach(([key, value]) => {
          if (!["base", "account", "address"].includes(key))
            setError(key, { type: "custom", message: value.join(", ") });
        });
      }
      toast.error(errorMessage);
    },
  });

  // Form
  const methods = useForm<any>({ mode: "onChange" });
  const {
    formState: { dirtyFields, errors },
    reset,
    setError,
  } = methods;
  const onReset = useCallback(() => reset({}), [reset]);
  const onFormSubmit = (data: any) => mutation.mutate({ data: data });

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onFormSubmit)}
        noValidate
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1rem 1.5rem",
          }}
        >
          <Typography variant="h6">Nouvel intérimaire</Typography>
        </Box>
        <Box sx={{ p: "1rem 1.5rem" }}>
          <Box>
            <Typography>Informations générales</Typography>
            <CreateForm />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              my: 3,
              gap: "1rem",
            }}
          >
            <Button
              variant="contained"
              onClick={onReset}
              sx={{ backgroundColor: "text.secondary" }}
              disabled={
                mutation.isLoading || Object.keys(dirtyFields).length === 0
              }
            >
              Annuler
            </Button>
            <Tooltip
              title={
                Object.keys(errors).length > 0
                  ? "Veuillez fixer les erreurs"
                  : Object.keys(dirtyFields).length === 0
                  ? "Aucun champs a été modifié"
                  : ""
              }
            >
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ color: "white" }}
                  disabled={
                    mutation.isLoading ||
                    Object.keys(errors).length > 0 ||
                    Object.keys(dirtyFields).length === 0
                  }
                >
                  {mutation.isLoading && <CircularProgress size={10} />}
                  Sauvegarder
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
}
